import { formatReportsMetricValue } from '@/utils/reportsMetrics';
import { buildFileTimestampSegment } from '@/utils/fileDownload';
import {
  REPORTS_SALES_TEXT,
  requireReportsSalesTextValue,
} from '@/composables/reports/sales/useReportsSalesPresentation';

export const REPORTS_INCOMES_RECEIVABLES_FALLBACK_REPORT_CODE = 'incomes_due';
export const REPORTS_INCOMES_RECEIVABLES_EXPORTABLE_CODES = ['incomes_due', 'incomes_received', 'incomes_all'];

const ALLOWED_REPORT_CODES = [
  'incomes_due',
  'incomes_received',
  'incomes_all',
];

const INCOMES_RECEIVABLES_DEFAULT_HEADER_TITLE = 'Reportes de cobranza';

const METRIC_VISUAL_BY_CODE = {
  total_due: { icon: 'fa-line-chart', iconBg: 'rgba(0,112,205,0.14)', iconColor: '#0070cd' },
  overdue_total: { icon: 'fa-exclamation-triangle', iconBg: 'rgba(239,68,68,0.14)', iconColor: '#b91c1c' },
  due_soon_total: { icon: 'fa-clock-o', iconBg: 'rgba(250,204,21,0.2)', iconColor: '#b45309' },
  recovery_percentage: { icon: 'fa-area-chart', iconBg: 'rgba(59,212,46,0.14)', iconColor: '#1f9b43' },
  total_collected: { icon: 'fa-line-chart', iconBg: 'rgba(0,112,205,0.14)', iconColor: '#0070cd' },
  payments_count: { icon: 'fa-calculator', iconBg: 'rgba(250,204,21,0.2)', iconColor: '#b45309' },
  average_ticket: { icon: 'fa-area-chart', iconBg: 'rgba(59,212,46,0.14)', iconColor: '#1f9b43' },
  period_vs_previous_percentage: { icon: 'fa-line-chart', iconBg: 'rgba(0,112,205,0.14)', iconColor: '#0070cd' },
  total_generated: { icon: 'fa-line-chart', iconBg: 'rgba(0,112,205,0.14)', iconColor: '#0070cd' },
  total_pending: { icon: 'fa-clock-o', iconBg: 'rgba(250,204,21,0.2)', iconColor: '#b45309' },
  paid_percentage: { icon: 'fa-area-chart', iconBg: 'rgba(59,212,46,0.14)', iconColor: '#1f9b43' },
};

const FALLBACK_METRIC_VISUALS = [
  { icon: 'fa-line-chart', iconBg: 'rgba(0,112,205,0.14)', iconColor: '#0070cd' },
  { icon: 'fa-exclamation-triangle', iconBg: 'rgba(239,68,68,0.14)', iconColor: '#b91c1c' },
  { icon: 'fa-clock-o', iconBg: 'rgba(250,204,21,0.2)', iconColor: '#b45309' },
  { icon: 'fa-area-chart', iconBg: 'rgba(59,212,46,0.14)', iconColor: '#1f9b43' },
];

const exportSegmentsByReport = {
  incomes_due: 'cobranza_pagos_por_cobrar',
  incomes_received: 'cobranza_pagos_recibidos',
  incomes_all: 'cobranza_todos_los_pagos',
  default: 'reporte_cobranza',
};

export const normalizeReportsIncomesReceivablesReportCode = (value) => {
  const normalized = String(value || '').trim();

  if (ALLOWED_REPORT_CODES.includes(normalized)) {
    return normalized;
  }

  return REPORTS_INCOMES_RECEIVABLES_FALLBACK_REPORT_CODE;
};

export const normalizeReportsIncomesReceivablesTabs = (rawTabs = []) => {
  return (Array.isArray(rawTabs) ? rawTabs : [])
    .map((tab) => {
      const code = normalizeReportsIncomesReceivablesReportCode(tab?.code);
      const name = String(tab?.name ?? '').trim();
      const description = String(tab?.description ?? '').trim();

      if (name === '') {
        return null;
      }

      return {
        key: code,
        label: name,
        description,
        headerTitle: INCOMES_RECEIVABLES_DEFAULT_HEADER_TITLE,
        isDefault: tab?.is_default === true || Number(tab?.is_default) === 1,
      };
    })
    .filter(Boolean);
};

export const normalizeReportsIncomesReceivablesMetricCards = (rawCards = []) => {
  return (Array.isArray(rawCards) ? rawCards : []).map((card, index) => {
    const normalizedCode = String(card?.code || '').trim();
    const visual = METRIC_VISUAL_BY_CODE[normalizedCode]
      || FALLBACK_METRIC_VISUALS[index % FALLBACK_METRIC_VISUALS.length];

    return {
      title: String(card?.name ?? '').trim(),
      value: formatReportsMetricValue(card?.value, card?.format),
      valueMeta: String(card?.description ?? '').trim(),
      icon: visual.icon,
      iconBg: visual.iconBg,
      iconColor: visual.iconColor,
    };
  });
};

export const buildReportsIncomesReceivablesHeaderActions = () => {
  return [
    {
      key: 'excel',
      label: requireReportsSalesTextValue(REPORTS_SALES_TEXT.page.headerActions.excel, 'reports.sales.page.headerActions.excel'),
      icon: 'fa-file-excel-o',
      variant: 'secondary',
    },
    {
      key: 'pdf',
      label: requireReportsSalesTextValue(REPORTS_SALES_TEXT.page.headerActions.pdf, 'reports.sales.page.headerActions.pdf'),
      icon: 'fa-file-pdf-o',
      variant: 'secondary',
    },
  ];
};

export const buildReportsIncomesReceivablesExportFileName = (reportCode, extension) => {
  const normalizedReportCode = normalizeReportsIncomesReceivablesReportCode(reportCode);
  const normalizedExtension = String(extension || '').trim().toLowerCase();
  const baseSegment = exportSegmentsByReport[normalizedReportCode] || exportSegmentsByReport.default;

  return `${baseSegment}_${buildFileTimestampSegment()}.${normalizedExtension}`;
};

export const buildReportsIncomesReceivablesFallbackTab = () => {
  return {
    key: REPORTS_INCOMES_RECEIVABLES_FALLBACK_REPORT_CODE,
    label: 'Pagos por cobrar',
    description: 'Seguimiento de pagos pendientes por recibir.',
    headerTitle: INCOMES_RECEIVABLES_DEFAULT_HEADER_TITLE,
    isDefault: true,
  };
};

export const buildReportsIncomesReceivablesHeaderSubtitle = (tabLabel) => {
  const normalizedTabLabel = String(tabLabel || '').trim();
  return normalizedTabLabel !== '' ? `Reporte de ${normalizedTabLabel}` : 'Reporte de cobranza';
};
